import React, { useState } from "react";
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography,
} from "@mui/material";
import { Check, CopyAll } from "@mui/icons-material";
import { credentialRows, formatCreatorCredentials } from "./creatorCredentials";

export async function copyToClipboard(text) {
    if (!text) return false;
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch (err) {
        console.error("Copy to clipboard failed:", err);
        return false;
    }
}

export function CreatorCredentialsCard({ credentials }) {
    const [copiedKey, setCopiedKey] = useState("");
    const rows = credentialRows(credentials);

    const copyText = async (key, text) => {
        const ok = await copyToClipboard(text);
        if (!ok) return;
        setCopiedKey(key);
        setTimeout(() => setCopiedKey(""), 1500);
    };

    if (!rows.length) {
        return (
            <Typography fontSize={13} color="#888">
                No login details available for this creator.
            </Typography>
        );
    }

    return (
        <Box
            textAlign="left"
            sx={{
                border: "1px solid #eceff1",
                borderRadius: 2,
                p: 2,
                bgcolor: "#fafbfc",
            }}
        >
            <Box display="flex" alignItems="center" justifyContent="flex-end" mb={0.5}>
                <Button
                    onClick={() => copyText("all", formatCreatorCredentials(credentials))}
                    startIcon={copiedKey === "all" ? <Check fontSize="small" /> : <CopyAll fontSize="small" />}
                    sx={{ textTransform: "none", borderRadius: 2, color: copiedKey === "all" ? "#22c55e" : "#12131A" }}
                >
                    {copiedKey === "all" ? "Copied" : "Copy all"}
                </Button>
            </Box>
            {rows.map((row) => {
                const display = row.value || "—";
                const canCopy = Boolean(row.value);
                return (
                    <Box
                        key={row.key}
                        onClick={() => canCopy && copyText(row.key, row.value)}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            py: 1,
                            borderTop: "1px solid #eceff1",
                            cursor: canCopy ? "pointer" : "default",
                            "&:hover": canCopy ? { bgcolor: "#fff" } : undefined,
                        }}
                    >
                        <Box flex={1} minWidth={0}>
                            <Typography fontSize={11} fontWeight={700} color="#888" letterSpacing={0.4}>
                                {row.label.toUpperCase()}
                            </Typography>
                            <Typography
                                fontSize={14}
                                color={row.value ? "#12131A" : "#9CA3AF"}
                                sx={{ wordBreak: "break-all", fontFamily: row.key === "password" ? "monospace" : "inherit" }}
                            >
                                {display}
                            </Typography>
                        </Box>
                        <IconButton size="small" aria-label={`Copy ${row.label}`} disabled={!canCopy}>
                            {copiedKey === row.key
                                ? <Check fontSize="small" sx={{ color: "#22c55e" }} />
                                : <CopyAll fontSize="small" />}
                        </IconButton>
                    </Box>
                );
            })}
        </Box>
    );
}

export function CopyCredentialsButton({ credentials, label = "Copy credentials", size = "small" }) {
    const [open, setOpen] = useState(false);
    const name = credentials?.displayName || credentials?.username || "Creator";

    return (
        <>
            <Button
                size={size}
                variant="outlined"
                startIcon={<CopyAll fontSize="small" />}
                onClick={() => setOpen(true)}
                sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: 2,
                    whiteSpace: "nowrap",
                    color: "#374151",
                    borderColor: "#E5E7EB",
                    "&:hover": { borderColor: "#D1D5DB", background: "#F9FAFB" },
                }}
            >
                {label}
            </Button>
            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 700, fontSize: 18 }}>
                    {name} - Login details
                </DialogTitle>
                <DialogContent sx={{ pb: 1 }}>
                    <CreatorCredentialsCard credentials={credentials} />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setOpen(false)} sx={{ textTransform: "none" }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
